/**
 * Project Markup layout document (v1 envelope + plan-on-canvas transform).
 *
 * Coordinate spaces:
 * - DRAWING: marker x/y are % of the floor-plan element
 * - CANVAS: callout / plan-info x/y are % of the fixed presentation canvas
 * - PLAN TRANSFORM: scale + center (x,y %) places the plan on the canvas
 *
 * Legacy docs only had workspaceView (whole-composition zoom/pan). Those upgrade
 * into planTransform; callouts are treated as canvas-space after upgrade.
 */

export type ProjectMarkupWorkspaceView = {
  scale: number;
  panX: number;
  panY: number;
};

/** Plan-only transform on the fixed presentation canvas. */
export type ProjectMarkupPlanTransform = {
  /** Uniform plan scale (1 = fit-contain baseline). */
  scale: number;
  /** Plan center X as % of canvas width. */
  x: number;
  /** Plan center Y as % of canvas height. */
  y: number;
  locked: boolean;
};

export type ProjectMarkupCanvasSize = {
  width: number;
  height: number;
};

export type ProjectMarkupPlanInformation = {
  title: string;
  preparedBy: string;
  date: string;
  /** Canvas-space % */
  x: number;
  /** Canvas-space % */
  y: number;
  width: number;
  height: number;
};

export type ProjectMarkupCoordinateModel = "legacy_overlay" | "plan_canvas_v2";

export type ProjectMarkupLayoutDocumentV1<TMarker = Record<string, unknown>> = {
  version: 1;
  markers: TMarker[];
  hiddenPages: number[];
  /** @deprecated Kept for backward compatibility; prefer planTransform. */
  workspaceView: ProjectMarkupWorkspaceView;
  planTransform: ProjectMarkupPlanTransform;
  canvas: ProjectMarkupCanvasSize;
  /**
   * legacy_overlay: calloutX/Y were plan-% (pre-architecture fix).
   * plan_canvas_v2: calloutX/Y are canvas-%.
   */
  coordinateModel: ProjectMarkupCoordinateModel;
  planInformation: ProjectMarkupPlanInformation | null;
};

export type ProjectMarkupLayoutDocument<TMarker = Record<string, unknown>> =
  ProjectMarkupLayoutDocumentV1<TMarker>;

/**
 * Place the plan (natural aspect) on the canvas.
 * `planTransform.scale === 1` means contain-fit inside the canvas with PLAN_FIT_MARGIN.
 * Callouts / connectors / Plan Information never affect this calculation.
 */
export const PLAN_FIT_MARGIN = 0.06;

/** Identity scale for Fit Plan — largest contain-fit size (not a magic shrink factor). */
export const FIT_PLAN_SCALE = 1;

export const DEFAULT_PROJECT_MARKUP_WORKSPACE_VIEW: ProjectMarkupWorkspaceView = {
  scale: 1,
  panX: 0,
  panY: 0,
};

export const DEFAULT_PROJECT_MARKUP_PLAN_TRANSFORM: ProjectMarkupPlanTransform = {
  scale: FIT_PLAN_SCALE,
  x: 50,
  y: 50,
  locked: false,
};

/** Fixed presentation canvas (export + editor composition). Landscape A3-ish @ ~96dpi. */
export const DEFAULT_PROJECT_MARKUP_CANVAS: ProjectMarkupCanvasSize = {
  width: 1600,
  height: 1131,
};

export const DEFAULT_PROJECT_MARKUP_PLAN_INFO_SIZE = {
  width: 220,
  height: 84,
};

export function createDefaultProjectMarkupLayoutDocument<TMarker = Record<string, unknown>>(
  markers: TMarker[] = [],
): ProjectMarkupLayoutDocument<TMarker> {
  return {
    version: 1,
    markers,
    hiddenPages: [],
    workspaceView: { ...DEFAULT_PROJECT_MARKUP_WORKSPACE_VIEW },
    planTransform: { ...DEFAULT_PROJECT_MARKUP_PLAN_TRANSFORM },
    canvas: { ...DEFAULT_PROJECT_MARKUP_CANVAS },
    coordinateModel: "plan_canvas_v2",
    planInformation: null,
  };
}

function sanitizeNumber(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function sanitizeWorkspaceView(raw: unknown): ProjectMarkupWorkspaceView {
  const obj = typeof raw === "object" && raw ? (raw as Record<string, unknown>) : {};
  return {
    scale: Math.min(4, Math.max(0.25, sanitizeNumber(obj.scale, 1))),
    panX: Math.min(200, Math.max(-200, sanitizeNumber(obj.panX, 0))),
    panY: Math.min(200, Math.max(-200, sanitizeNumber(obj.panY, 0))),
  };
}

function sanitizePlanTransform(
  raw: unknown,
  _legacyView: ProjectMarkupWorkspaceView,
): ProjectMarkupPlanTransform {
  const obj = typeof raw === "object" && raw ? (raw as Record<string, unknown>) : null;
  if (obj) {
    return {
      scale: Math.min(4, Math.max(0.25, sanitizeNumber(obj.scale, FIT_PLAN_SCALE))),
      x: Math.min(100, Math.max(0, sanitizeNumber(obj.x, 50))),
      y: Math.min(100, Math.max(0, sanitizeNumber(obj.y, 50))),
      locked: Boolean(obj.locked),
    };
  }
  // Legacy whole-composition zoom must not shrink the plan — Fit + Centre.
  return fitPlanTransform();
}

function sanitizeCanvas(raw: unknown): ProjectMarkupCanvasSize {
  const obj = typeof raw === "object" && raw ? (raw as Record<string, unknown>) : {};
  return {
    width: Math.max(640, Math.round(sanitizeNumber(obj.width, DEFAULT_PROJECT_MARKUP_CANVAS.width))),
    height: Math.max(480, Math.round(sanitizeNumber(obj.height, DEFAULT_PROJECT_MARKUP_CANVAS.height))),
  };
}

function sanitizeHiddenPages(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  const pages = raw
    .map((v) => Math.floor(sanitizeNumber(v, NaN)))
    .filter((v) => Number.isFinite(v) && v >= 1);
  return Array.from(new Set(pages)).sort((a, b) => a - b);
}

function sanitizePlanInformation(raw: unknown): ProjectMarkupPlanInformation | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const title = String(obj.title ?? "").trim();
  const preparedBy = String(obj.preparedBy ?? "").trim();
  const date = String(obj.date ?? "").trim();
  if (!title && !preparedBy && !date) return null;
  return {
    title,
    preparedBy,
    date,
    x: Math.min(100, Math.max(0, sanitizeNumber(obj.x, 50))),
    y: Math.min(100, Math.max(0, sanitizeNumber(obj.y, 10))),
    width: Math.max(140, sanitizeNumber(obj.width, DEFAULT_PROJECT_MARKUP_PLAN_INFO_SIZE.width)),
    height: Math.max(56, sanitizeNumber(obj.height, DEFAULT_PROJECT_MARKUP_PLAN_INFO_SIZE.height)),
  };
}

/** Sync deprecated workspaceView mirror from planTransform for older readers. */
export function workspaceViewFromPlanTransform(
  plan: ProjectMarkupPlanTransform,
): ProjectMarkupWorkspaceView {
  return {
    scale: plan.scale,
    panX: (plan.x - 50) * 4,
    panY: (plan.y - 50) * 4,
  };
}

export function parseProjectMarkupLayoutDocument<TMarker = Record<string, unknown>>(
  raw: unknown,
): ProjectMarkupLayoutDocument<TMarker> {
  if (Array.isArray(raw)) {
    // Bare legacy marker arrays pre-date canvas-space callouts; flag for migration.
    return { ...createDefaultProjectMarkupLayoutDocument(raw as TMarker[]), coordinateModel: "legacy_overlay" };
  }
  if (!raw || typeof raw !== "object") {
    return createDefaultProjectMarkupLayoutDocument();
  }
  const obj = raw as Record<string, unknown>;
  const markers = Array.isArray(obj.markers) ? (obj.markers as TMarker[]) : [];
  const workspaceView = sanitizeWorkspaceView(obj.workspaceView);
  const hasExplicitPlanTransform =
    typeof obj.planTransform === "object" && obj.planTransform !== null;
  const planTransform = sanitizePlanTransform(obj.planTransform, workspaceView);
  const coordinateModel: ProjectMarkupCoordinateModel =
    obj.coordinateModel === "plan_canvas_v2" || hasExplicitPlanTransform
      ? "plan_canvas_v2"
      : "legacy_overlay";
  return {
    version: 1,
    markers,
    hiddenPages: sanitizeHiddenPages(obj.hiddenPages),
    workspaceView: workspaceViewFromPlanTransform(planTransform),
    planTransform,
    canvas: sanitizeCanvas(obj.canvas),
    coordinateModel,
    planInformation: sanitizePlanInformation(obj.planInformation),
  };
}

export function serializeProjectMarkupLayoutDocument<TMarker = Record<string, unknown>>(
  doc: ProjectMarkupLayoutDocument<TMarker>,
): ProjectMarkupLayoutDocument<TMarker> {
  return parseProjectMarkupLayoutDocument<TMarker>(doc);
}

/**
 * Contain-fit factor from PDF natural size → canvas avail box (margins only; no callouts).
 */
export function computeContainFitFactor(
  canvas: ProjectMarkupCanvasSize,
  planNaturalWidth: number,
  planNaturalHeight: number,
  margin: number = PLAN_FIT_MARGIN,
): number {
  const cw = Math.max(1, canvas.width);
  const ch = Math.max(1, canvas.height);
  const nw = Math.max(1, planNaturalWidth);
  const nh = Math.max(1, planNaturalHeight);
  const m = Math.min(0.2, Math.max(0.02, margin));
  const availW = cw * (1 - m * 2);
  const availH = ch * (1 - m * 2);
  return Math.min(availW / nw, availH / nh);
}

export function fitPlanTransform(
  current?: Pick<ProjectMarkupPlanTransform, "locked"> | null,
): ProjectMarkupPlanTransform {
  return {
    scale: FIT_PLAN_SCALE,
    x: 50,
    y: 50,
    locked: Boolean(current?.locked),
  };
}

export function computePlanRectOnCanvas(input: {
  canvas: ProjectMarkupCanvasSize;
  planNaturalWidth: number;
  planNaturalHeight: number;
  planTransform: ProjectMarkupPlanTransform;
  /** Margin fraction at Fit Plan (default PLAN_FIT_MARGIN ≈ 6%). */
  fitMargin?: number;
}): { left: number; top: number; width: number; height: number } {
  const margin = input.fitMargin ?? PLAN_FIT_MARGIN;
  const cw = Math.max(1, input.canvas.width);
  const ch = Math.max(1, input.canvas.height);
  const nw = Math.max(1, input.planNaturalWidth);
  const nh = Math.max(1, input.planNaturalHeight);
  const fit = computeContainFitFactor(input.canvas, nw, nh, margin);
  const width = nw * fit * input.planTransform.scale;
  const height = nh * fit * input.planTransform.scale;
  const cx = (input.planTransform.x / 100) * cw;
  const cy = (input.planTransform.y / 100) * ch;
  return {
    left: cx - width / 2,
    top: cy - height / 2,
    width,
    height,
  };
}

/** Map a drawing-space % point into canvas pixel coordinates. */
export function drawingPctToCanvasPx(
  xPct: number,
  yPct: number,
  planRect: { left: number; top: number; width: number; height: number },
): { x: number; y: number } {
  return {
    x: planRect.left + (xPct / 100) * planRect.width,
    y: planRect.top + (yPct / 100) * planRect.height,
  };
}

/** Map canvas % into canvas pixels. */
export function canvasPctToPx(
  xPct: number,
  yPct: number,
  canvas: ProjectMarkupCanvasSize,
): { x: number; y: number } {
  return {
    x: (xPct / 100) * canvas.width,
    y: (yPct / 100) * canvas.height,
  };
}
